import { useCallback, useEffect, useMemo, useState } from 'react';
import to from 'await-to-js';
import apiClient from 'c-data/apiClient';
import { useEntityData } from 'c-data';
import { useAPIClientRequest } from 'c-hooks';
import type {
    Campaign,
    CampaignInsertionOrder,
    CampaignInsertionOrderPlaceholder,
    InsertionOrderDetailResponse,
    InsertionOrderKey,
    InsertionOrderPlaceholderPayload,
    InsertionOrderRule,
} from 'c-sdk';
import { insertionOrderKey, placeholderRow } from './lib';

/**
 * UL-7912 -- the DSP schedule page's own data: attached insertion orders, rules, and the allowed
 * venue count, plus the actions that change them.
 *
 * Plain hook state, deliberately NOT the module-level jotai atoms `useNewCampaignSchedule` keeps.
 * Those are shared by every consumer on the page, so anything written there would bleed into the
 * direct view; this state lives and dies with the DSP container.
 *
 * Schedules themselves still come from `useNewCampaignSchedule`, because the shared right rail reads
 * them from there.
 */
const useDspTargeting = (campaignId: number) => {
    const [insertionOrders, setInsertionOrders] = useState<CampaignInsertionOrder[]>([]);
    const [rules, setRules] = useState<InsertionOrderRule[]>([]);
    const [placeholders, setPlaceholders] = useState<CampaignInsertionOrderPlaceholder[]>([]);
    const [venueCount, setVenueCount] = useState(0);
    const [venuesUploadedAt, setVenuesUploadedAt] = useState<string | null>(null);

    const ioRequest = useAPIClientRequest(apiClient.Entities.Campaign.insertionOrders);
    const rulesRequest = useAPIClientRequest(apiClient.Entities.Campaign.insertionOrderRules);
    const venuesRequest = useAPIClientRequest(apiClient.Entities.Campaign.allowedVenues);
    const syncRequest = useAPIClientRequest(apiClient.Entities.Campaign.syncInsertionOrders);
    const applyRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.applyInsertionOrderSchedule,
    );
    const removeRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.removeInsertionOrderSchedule,
    );
    const cpmRequest = useAPIClientRequest(apiClient.Entities.Campaign.updateInsertionOrderDetail);
    const targetRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.updateInsertionOrderDetail,
    );
    const campaignRequest = useAPIClientRequest(apiClient.Entities.Campaign.get);
    const placeholdersRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.insertionOrderPlaceholders,
    );
    const createPlaceholderRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.createInsertionOrderPlaceholder,
    );
    const updatePlaceholderRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.updateInsertionOrderPlaceholder,
    );
    const deletePlaceholderRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.deleteInsertionOrderPlaceholder,
    );
    const linkPlaceholderRequest = useAPIClientRequest(
        apiClient.Entities.Campaign.linkInsertionOrderPlaceholder,
    );
    const { upsertEntity: upsertCampaign } = useEntityData<Campaign>('Campaign');

    const { start: startIos } = ioRequest;
    const { start: startRules } = rulesRequest;
    const { start: startVenues } = venuesRequest;
    const { start: startPlaceholders } = placeholdersRequest;

    /**
     * Attached insertion orders, rules and planned lines always move together: the table joins
     * them, and a schedule change can move a planned line's chip as well as an IO's.
     */
    const refresh = useCallback(async () => {
        const [[ioErr, ioRes], [ruleErr, ruleRes], [plErr, plRes]] = await Promise.all([
            to(startIos(campaignId)),
            to(startRules(campaignId)),
            to(startPlaceholders(campaignId)),
        ]);

        if (!ioErr && Array.isArray(ioRes?.data?.data)) setInsertionOrders(ioRes.data.data);
        if (!ruleErr && Array.isArray(ruleRes?.data?.data)) setRules(ruleRes.data.data);
        if (!plErr && Array.isArray(plRes?.data?.data)) setPlaceholders(plRes.data.data);
    }, [campaignId, startIos, startPlaceholders, startRules]);

    const refreshVenues = useCallback(async () => {
        const [err, res] = await to(startVenues(campaignId));

        if (!err && res?.data) {
            setVenueCount(res.data.count ?? 0);
            setVenuesUploadedAt(res.data.uploaded_at ?? null);
        }
    }, [campaignId, startVenues]);

    useEffect(() => {
        setInsertionOrders([]);
        setRules([]);
        setPlaceholders([]);
        refresh();
        refreshVenues();
    }, [refresh, refreshVenues]);

    const rulesById = useMemo(() => new Map(rules.map(rule => [rule.id, rule])), [rules]);

    const byKey = useMemo(
        () => new Map(insertionOrders.map(io => [insertionOrderKey(io), io])),
        [insertionOrders],
    );

    /**
     * UL-8136 -- planned lines as table rows, keyed by their reserved schedule key, so they can be
     * ticked and scheduled exactly like insertion orders.
     */
    const placeholderRows = useMemo(
        () => placeholders.map(placeholder => placeholderRow(placeholder, campaignId)),
        [campaignId, placeholders],
    );

    /** Every row a schedule can be applied to: planned lines and insertion orders. */
    const scheduleRowsByKey = useMemo(
        () =>
            new Map(
                [...placeholderRows, ...insertionOrders].map(row => [insertionOrderKey(row), row]),
            ),
        [insertionOrders, placeholderRows],
    );

    const { start: startSync } = syncRequest;
    const sync = useCallback(async () => {
        const [err] = await to(startSync(campaignId));
        if (!err) await refresh();
        return !err;
    }, [campaignId, refresh, startSync]);

    const { start: startApply } = applyRequest;
    const applySchedule = useCallback(
        async (scheduleId: number, keys: InsertionOrderKey[]) => {
            if (keys.length === 0) return false;
            const [err] = await to(
                startApply(campaignId, { schedule_id: scheduleId, insertion_orders: keys }),
            );
            if (!err) await refresh();
            return !err;
        },
        [campaignId, refresh, startApply],
    );

    const { start: startRemove } = removeRequest;
    /**
     * Return insertion orders to the Default. With `onlyFromScheduleId`, only those currently on
     * that schedule move -- which is what － on a particular schedule means.
     */
    const returnToDefault = useCallback(
        async (keys: InsertionOrderKey[], onlyFromScheduleId?: number) => {
            if (keys.length === 0) return false;
            const [err] = await to(
                startRemove(campaignId, {
                    insertion_orders: keys,
                    ...(onlyFromScheduleId != null ? { schedule_id: onlyFromScheduleId } : {}),
                }),
            );
            if (!err) await refresh();
            return !err;
        },
        [campaignId, refresh, startRemove],
    );

    const { start: startCampaign } = campaignRequest;
    /**
     * UL-8133 -- the campaign's Expected Impressions is the sum of its targets, so anything that
     * moves a target must move the campaign too, or Reporting Inputs shows a stale figure until a
     * reload. When the answer already carries the new sum it is applied directly; otherwise the
     * campaign is fetched again.
     */
    const refreshCampaign = useCallback(
        async (expectedImpacts?: number | null) => {
            if (expectedImpacts !== undefined) {
                upsertCampaign({ id: campaignId, expected_impacts: expectedImpacts } as Campaign);
                return;
            }
            const [err, res] = await to(startCampaign(campaignId));
            if (!err && res?.data?.data) upsertCampaign(res.data.data);
        },
        [campaignId, startCampaign, upsertCampaign],
    );

    /** The detail endpoint answers with the campaign's new sum; undefined when it did not say. */
    const expectedFrom = (res: { data?: InsertionOrderDetailResponse } | null | undefined) =>
        res?.data?.data?.expected_impacts;

    const { start: startCpm } = cpmRequest;
    const updateCpm = useCallback(
        async (key: InsertionOrderKey, cpm: number | null) => {
            const [err, res] = await to(startCpm(campaignId, { ...key, cpm }));
            if (!err) {
                // Only the one cell changed; avoid refetching the whole table for it.
                setInsertionOrders(current =>
                    current.map(io =>
                        insertionOrderKey(io) === insertionOrderKey(key)
                            ? { ...io, cpm: cpm === null ? null : cpm.toFixed(4) }
                            : io,
                    ),
                );
                const expected = expectedFrom(res);
                if (expected !== undefined) await refreshCampaign(expected);
            }
            return !err;
        },
        [campaignId, refreshCampaign, startCpm],
    );

    const { start: startTarget } = targetRequest;
    /**
     * UL-8133 -- sends only the target, so the CPM is untouched (the API writes only the keys
     * sent). The row takes the target the API answered with, and the campaign its new sum.
     */
    const updateImpressionsTarget = useCallback(
        async (key: InsertionOrderKey, target: number | null) => {
            const [err, res] = await to(
                startTarget(campaignId, { ...key, impressions_target: target }),
            );
            if (!err) {
                const saved = res?.data?.data?.impressions_target;
                setInsertionOrders(current =>
                    current.map(io =>
                        insertionOrderKey(io) === insertionOrderKey(key)
                            ? { ...io, impressions_target: saved !== undefined ? saved : target }
                            : io,
                    ),
                );
                await refreshCampaign(expectedFrom(res));
            }
            return !err;
        },
        [campaignId, refreshCampaign, startTarget],
    );

    const { start: startCreatePlaceholder } = createPlaceholderRequest;
    /** UL-8136 -- a new planned line goes on the Default; its target counts straight away. */
    const createPlaceholder = useCallback(
        async (label: string, target: number | null) => {
            const [err, res] = await to(
                startCreatePlaceholder(campaignId, {
                    label,
                    ...(target !== null ? { impressions_target: target } : {}),
                }),
            );
            if (!err) {
                await Promise.all([refresh(), refreshCampaign(res?.data?.data?.expected_impacts)]);
            }
            return !err;
        },
        [campaignId, refresh, refreshCampaign, startCreatePlaceholder],
    );

    const { start: startUpdatePlaceholder } = updatePlaceholderRequest;
    /** Sends only the keys given (the label or the target), as the detail endpoint does. */
    const updatePlaceholder = useCallback(
        async (placeholderId: number, payload: InsertionOrderPlaceholderPayload) => {
            const [err, res] = await to(startUpdatePlaceholder(campaignId, placeholderId, payload));
            if (!err) {
                const saved = res?.data?.data;
                setPlaceholders(current =>
                    current.map(placeholder =>
                        placeholder.id === placeholderId
                            ? {
                                  ...placeholder,
                                  ...payload,
                                  ...(saved
                                      ? {
                                            label: saved.label,
                                            impressions_target: saved.impressions_target,
                                            schedule_id: saved.schedule_id,
                                        }
                                      : {}),
                              }
                            : placeholder,
                    ),
                );
                if ('impressions_target' in payload) {
                    await refreshCampaign(saved?.expected_impacts);
                }
            }
            return !err;
        },
        [campaignId, refreshCampaign, startUpdatePlaceholder],
    );

    const { start: startDeletePlaceholder } = deletePlaceholderRequest;
    /** Removes the planned line, and with it its target from the campaign. */
    const deletePlaceholder = useCallback(
        async (placeholderId: number) => {
            const [err, res] = await to(startDeletePlaceholder(campaignId, placeholderId));
            if (!err) {
                setPlaceholders(current => current.filter(p => p.id !== placeholderId));
                await refreshCampaign(res?.data?.data?.expected_impacts);
            }
            return !err;
        },
        [campaignId, refreshCampaign, startDeletePlaceholder],
    );

    const { start: startLinkPlaceholder } = linkPlaceholderRequest;
    /**
     * Moves the planned line's target and schedule onto one catalogue insertion order. The API
     * attaches the IO itself, so no rule is written here. Answers null on success, or the reason
     * it was refused (a 422: that IO already has a target, or was not found).
     */
    const linkPlaceholder = useCallback(
        async (placeholderId: number, key: InsertionOrderKey): Promise<string | null> => {
            const [err, res] = await to(startLinkPlaceholder(campaignId, placeholderId, key));
            // The API's 422 message ("This insertion order already has a target", or not found).
            if (err) return String((err as { message?: unknown })?.message ?? '');
            await Promise.all([refresh(), refreshCampaign(res?.data?.data?.expected_impacts)]);
            return null;
        },
        [campaignId, refresh, refreshCampaign, startLinkPlaceholder],
    );

    return {
        insertionOrders,
        placeholders,
        placeholderRows,
        scheduleRowsByKey,
        rules,
        rulesById,
        byKey,
        venueCount,
        venuesUploadedAt,

        refresh,
        refreshVenues,
        sync,
        applySchedule,
        returnToDefault,
        updateCpm,
        updateImpressionsTarget,
        refreshCampaign,
        createPlaceholder,
        updatePlaceholder,
        deletePlaceholder,
        linkPlaceholder,

        isLoading: ioRequest.isLoading || rulesRequest.isLoading,
        loadError: ioRequest.error ?? rulesRequest.error,
        isSyncing: syncRequest.isLoading,
        isScheduling: applyRequest.isLoading || removeRequest.isLoading,
        scheduleError: applyRequest.error ?? removeRequest.error,
        isSavingCpm: cpmRequest.isLoading,
        cpmError: cpmRequest.error,
        isSavingTarget: targetRequest.isLoading,
        targetError: targetRequest.error,
        placeholderError:
            createPlaceholderRequest.error ??
            updatePlaceholderRequest.error ??
            deletePlaceholderRequest.error,
        isSavingPlaceholder:
            createPlaceholderRequest.isLoading ||
            updatePlaceholderRequest.isLoading ||
            deletePlaceholderRequest.isLoading,
    };
};

export type DspTargeting = ReturnType<typeof useDspTargeting>;

export default useDspTargeting;
